import {
    ValueDescriptionAnchor,
    ValueDescriptionVersion,
} from 'src/models/anchor-and-version/value-description.model';

export interface SignalValueDescriptionRow {
    anchorKey?: number;
    comment: string;
    description: string;
    isActive: boolean;
    isNew: boolean;
    name: string;
    originalDescription?: string;
    originalIsActive?: boolean;
    originalName?: string;
    originalValue?: number | null;
    rowId: string;
    value: number | null;
}

export type ExistingSignalValueDescriptionRow = SignalValueDescriptionRow & {
    anchorKey: number;
};

export function createNewValueDescriptionRow(rowId: string): SignalValueDescriptionRow {
    return {
        comment: '',
        description: '',
        isActive: true,
        isNew: true,
        name: '',
        rowId,
        value: null,
    };
}

export function createRowsFromAnchors(
    anchors: ValueDescriptionAnchor[]
): SignalValueDescriptionRow[] {
    return anchors
        .map((anchor) => createRow(anchor))
        .filter((row): row is SignalValueDescriptionRow => row !== null)
        .sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
}

export function createValueDescriptionAnchorPayload(
    row: SignalValueDescriptionRow,
    signalAnchorKey: number,
    changeRequestKey: number
): Record<string, unknown> {
    return {
        logText: '',
        signalAnchorKey,
        allVersions: [createValueDescriptionVersionPayload(row, 0, changeRequestKey)],
    };
}

export function createValueDescriptionVersionPayload(
    row: SignalValueDescriptionRow,
    anchorKey: number,
    changeRequestKey: number
): Record<string, unknown> {
    return {
        anchorKey,
        changeRequestKey,
        comment: row.comment,
        description: row.description,
        isActive: Boolean(row.isActive),
        logText: '',
        name: row.name || row.description,
        value: row.value ?? 0,
    };
}

export function getDescriptionText(row: SignalValueDescriptionRow): string {
    return row.description || row.name || '';
}

export function getRowsToSave(rows: SignalValueDescriptionRow[]): {
    changedRows: ExistingSignalValueDescriptionRow[];
    newRows: SignalValueDescriptionRow[];
} {
    return {
        newRows: rows.filter((row) => row.isNew),
        changedRows: rows.filter(
            (row): row is ExistingSignalValueDescriptionRow =>
                !row.isNew && typeof row.anchorKey === 'number' && hasRowChanged(row)
        ),
    };
}

export function getValueDescriptionValidationMessage(
    rows: SignalValueDescriptionRow[]
): string | undefined {
    if (hasEmptyRequiredFields(rows)) {
        return 'Value description rows must have a value and description';
    }

    if (hasDuplicateValues(rows)) {
        return 'Value description values must be unique';
    }

    return undefined;
}

export function parseRowValue(value: unknown): number | null {
    const parsedValue =
        value === '' || value === null || value === undefined ? null : Number(value);
    return parsedValue === null || Number.isNaN(parsedValue) ? null : parsedValue;
}

function createRow(anchor: ValueDescriptionAnchor): SignalValueDescriptionRow | null {
    const latestVersion = anchor.validVersion ?? getNewestVersion(anchor.allVersions ?? []);
    if (!latestVersion) return null;

    const value = parseVersionValue(latestVersion.value);

    return {
        anchorKey: anchor.key,
        comment: latestVersion.comment ?? '',
        description: latestVersion.description ?? '',
        isActive: Boolean(latestVersion.isActive),
        isNew: false,
        name: latestVersion.name ?? '',
        originalDescription: latestVersion.description ?? '',
        originalIsActive: Boolean(latestVersion.isActive),
        originalName: latestVersion.name ?? '',
        originalValue: value,
        rowId: `existing-${anchor.key}`,
        value,
    };
}

function getNewestVersion(
    versions: ValueDescriptionVersion[]
): ValueDescriptionVersion | undefined {
    return [...versions].sort((a, b) => getVersionSortValue(b) - getVersionSortValue(a))[0];
}

function getVersionSortValue(version: ValueDescriptionVersion): number {
    const timestamp = version.createdTimestamp ? new Date(version.createdTimestamp).getTime() : 0;
    return timestamp || version.key || 0;
}

function hasDuplicateValues(rows: SignalValueDescriptionRow[]): boolean {
    const values = new Set<number>();

    return rows.some((row) => {
        if (row.value === null || row.value === undefined) return false;

        const value = Number(row.value);
        if (Number.isNaN(value)) return false;

        if (values.has(value)) return true;

        values.add(value);
        return false;
    });
}

function hasEmptyRequiredFields(rows: SignalValueDescriptionRow[]): boolean {
    return rows.some((row) => {
        const value = row.value;
        const hasEmptyValue = value === null || value === undefined || Number.isNaN(Number(value));
        const hasEmptyDescription = getDescriptionText(row).trim() === '';

        return hasEmptyValue || hasEmptyDescription;
    });
}

function hasRowChanged(row: SignalValueDescriptionRow): boolean {
    if (row.isNew) return true;

    return (
        row.value !== row.originalValue ||
        row.description !== row.originalDescription ||
        row.name !== row.originalName ||
        row.isActive !== row.originalIsActive
    );
}

function parseVersionValue(value: unknown): number | null {
    const parsedValue = typeof value === 'number' ? value : Number(value ?? 0);
    return Number.isNaN(parsedValue) ? null : parsedValue;
}
